import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Surface, Switch, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import {
  ConsentSettings,
  getStoredConsent,
  saveConsent,
} from '@/services/consent';
import { applyAnalyticsConsent } from '@/src/lib/analytics';
import { applySentryConsent } from '@/src/lib/sentryInit';

export function ConsentBanner() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  // Crash reports are opt-out (decisão 2026-07-02): toggle starts on.
  const [crashReports, setCrashReports] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    getStoredConsent().then((stored) => {
      if (!mounted) return;
      setVisible(stored === null);
      if (stored) {
        setAnalytics(stored.analytics);
        setCrashReports(stored.crashReports);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const persist = async (choice: Pick<ConsentSettings, 'analytics' | 'crashReports'>) => {
    setSaving(true);
    try {
      const consent = await saveConsent(choice);
      await Promise.all([
        applyAnalyticsConsent(consent),
        applySentryConsent(consent),
      ]);
      setVisible(false);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <View pointerEvents="box-none" style={styles.overlay}>
      <Surface elevation={5} style={[styles.banner, { backgroundColor: theme.colors.surface }]}>
        <Text variant="titleMedium" style={styles.title}>{t('privacy.consentTitle')}</Text>
        <Text variant="bodyMedium" style={styles.copy}>{t('privacy.consentBody')}</Text>

        <View style={styles.row}>
          <View style={styles.rowText}>
            <Text variant="bodyLarge">{t('privacy.analyticsLabel')}</Text>
            <Text variant="bodySmall">{t('privacy.analyticsDesc')}</Text>
          </View>
          <Switch value={analytics} onValueChange={setAnalytics} disabled={saving} />
        </View>

        <View style={styles.row}>
          <View style={styles.rowText}>
            <Text variant="bodyLarge">{t('privacy.crashLabel')}</Text>
            <Text variant="bodySmall">{t('privacy.crashDesc')}</Text>
          </View>
          <Switch value={crashReports} onValueChange={setCrashReports} disabled={saving} />
        </View>

        <View style={styles.actions}>
          <Button
            mode="text"
            onPress={() => persist({ analytics: false, crashReports: false })}
            disabled={saving}
          >
            {t('privacy.rejectAll')}
          </Button>
          <Button
            mode="outlined"
            onPress={() => persist({ analytics, crashReports })}
            disabled={saving}
          >
            {t('privacy.saveChoices')}
          </Button>
          <Button
            mode="contained"
            onPress={() => persist({ analytics: true, crashReports: true })}
            loading={saving}
            disabled={saving}
          >
            {t('privacy.acceptAll')}
          </Button>
        </View>
      </Surface>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    padding: 16,
  },
  banner: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 720,
    borderRadius: 16,
    padding: 16,
  },
  title: {
    marginBottom: 8,
    fontWeight: '700',
  },
  copy: {
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 16,
  },
  rowText: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
  },
});
